import { useEffect, useMemo, useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { Check, Globe } from "lucide-react";
import {
  FALLBACK_LANGUAGES,
  getCurrentLanguage,
  onLanguagesReady,
  setLanguage,
} from "../lib/googleTranslate";

const SEARCH_THRESHOLD = 12;

const LanguageSwitcher = ({ className = "" }) => {
  const [languages, setLanguages] = useState(FALLBACK_LANGUAGES);
  const [query, setQuery] = useState("");

  const current = getCurrentLanguage();

  useEffect(() => onLanguagesReady(setLanguages), []);

  const activeLabel = languages.find((l) => l.code === current)?.label ?? current;

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return languages;
    return languages.filter(
      ({ code, label }) =>
        label.toLowerCase().includes(needle) || code.toLowerCase().includes(needle)
    );
  }, [languages, query]);

  const showSearch = languages.length > SEARCH_THRESHOLD;

  return (
    // notranslate keeps Google from rewriting the language names, which would
    // leave a student unable to find their way back to their own language.
    //
    // Positioning is left entirely to the caller. This used to hardcode
    // `relative`, which silently beat the `absolute` that Header passes: both
    // have equal specificity, and Tailwind emits `.relative` after `.absolute`,
    // so the later rule won and the switcher dropped into normal flow on top of
    // the NYC logo. PopoverPanel uses `anchor`, so it portals and positions
    // itself and never needed a relative ancestor.
    <Popover as="div" className={`notranslate ${className}`} translate="no">
      <PopoverButton className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-active">
        <Globe aria-hidden="true" className="h-4 w-4" />
        <span>{activeLabel}</span>
      </PopoverButton>

      {/* `anchor` portals this panel onto document.body, outside the wrapper
          above, so it needs its own notranslate or Google rewrites every
          language name into the active language. */}
      <PopoverPanel
        anchor="bottom end"
        role="group"
        aria-label="Choose a language"
        translate="no"
        className="notranslate z-[10000] mt-1 flex max-h-80 w-60 flex-col rounded-md border border-border bg-white shadow-lg focus:outline-none"
      >
        {showSearch && (
          <div className="border-b border-border p-2">
            <input
              type="text"
              value={query}
              autoFocus
              aria-label="Search languages"
              placeholder="Search languages"
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded border border-border px-2 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-active"
            />
          </div>
        )}

        <div className="overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-500">No languages match.</p>
          ) : (
            filtered.map(({ code, label }) => (
              <button
                key={code}
                type="button"
                lang={code}
                aria-current={code === current}
                onClick={() => code !== current && setLanguage(code)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              >
                <span>{label}</span>
                {code === current && <Check aria-hidden="true" className="h-4 w-4" />}
              </button>
            ))
          )}
        </div>
      </PopoverPanel>
    </Popover>
  );
};

export default LanguageSwitcher;
